import { useEffect, useMemo, useState, type FC } from 'react';
import { LuX, LuPlus, LuArrowRight, LuArrowLeft } from 'react-icons/lu';
import { TRILHOS_PRONTOS, trilhoVazio, type TrilhoPronto } from '../data/trilhosProntos';
import type { ModeloTarefa, AcaoDesfecho } from '../services/ModeloTarefaService';
import { nomeEtapa } from '../data/etapas';
import './EscolherTrilhoModal.css';

/**
 * A biblioteca de trilhos (docs/propostas/tarefas-padrao.html §5, tela 4), no molde
 * dos Agentes prontos: passo 1 escolhe o trilho, passo 2 lê por extenso e confirma.
 * A modal não grava nada — devolve o trilho escolhido para quem a abriu.
 */
interface Props {
    aberta: boolean;
    onFechar: () => void;
    /** Trilho escolhido (ou o vazio, em "Começar do zero"). */
    onEscolher: (chave: string, etapas: ModeloTarefa[]) => Promise<void> | void;
    /** Há trilho em uso? Muda o título e o texto do botão. */
    substituindo?: boolean;
}

export const COR_ETAPA: Record<number, string> = {
    2: 'var(--pl-st-qual)', 3: 'var(--pl-st-win)', 4: 'var(--pl-st-prop)', 5: 'var(--pl-st-neg)', 6: 'var(--pl-st-lost)',
};

/** Frase curta do que a ação faz, para a leitura do passo 2 e para o Select do próximo passo. */
export const descreverAcao = (acao: AcaoDesfecho, statusDestinoId?: number | null, dias?: number | null) => {
    if (acao === 'move') return `move para ${nomeEtapa(statusDestinoId ?? 0)}`;
    if (acao === 'lose') return 'pede o motivo';
    if (acao === 'reschedule') return `reagenda +${dias ?? 1} ${dias === 1 ? 'dia útil' : 'dias úteis'}`;
    return 'encerra sem mover';
};

const EscolherTrilhoModal: FC<Props> = ({ aberta, onFechar, onEscolher, substituindo = false }) => {
    const [passo, setPasso] = useState<1 | 2>(1);
    const [chave, setChave] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!aberta) return;
        setPasso(1); setChave(null); setSalvando(false);
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            // Esc no passo 2 volta; no passo 1 fecha — como nos Agentes prontos.
            setPasso(p => { if (p === 2) return 1; onFechar(); return p; });
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [aberta, onFechar]);

    const escolhido: TrilhoPronto | null = useMemo(() => TRILHOS_PRONTOS.find(t => t.chave === chave) ?? null, [chave]);
    const doZero = chave === 'zero';

    if (!aberta) return null;

    const confirmar = async () => {
        if (!chave) return;
        setSalvando(true);
        try {
            await onEscolher(chave, doZero ? trilhoVazio() : escolhido!.etapas);
        } finally {
            setSalvando(false);
        }
    };

    const contar = (t: TrilhoPronto) => ({
        etapas: t.etapas.filter(e => e.ativo).length,
        desfechos: t.etapas.filter(e => e.ativo).reduce((n, e) => n + e.desfechos.length, 0),
    });

    return (
        <div className="tp-overlay" onClick={passo === 1 ? onFechar : undefined}>
            <div className="tp-dlg" role="dialog" aria-modal="true" aria-labelledby="tp-dlg-tit" onClick={e => e.stopPropagation()}>
                <div className="tp-dlg-head">
                    <div className="tp-dlg-txt">
                        <h2 id="tp-dlg-tit" className="tp-dlg-tit">{substituindo ? 'Trocar o trilho' : 'Escolher um trilho'}</h2>
                        <p className="tp-dlg-sub">
                            {passo === 1
                                ? 'O trilho é copiado para a sua empresa. Depois você ajusta títulos, prazos e desfechos como quiser.'
                                : doZero
                                    ? 'As três etapas nascem sem tarefa. Você liga e escreve cada uma na tela Tarefas padrão.'
                                    : `${escolhido?.nome}: o próximo passo que cada etapa cria, e o que cada desfecho faz.`}
                        </p>
                    </div>
                    <button type="button" className="tp-x" aria-label="Fechar" onClick={onFechar} disabled={salvando}><LuX size={16} /></button>
                </div>

                <div className="tp-dlg-body">
                    {passo === 1 ? (
                        <div className="tp-gal">
                            {TRILHOS_PRONTOS.map(t => {
                                const n = contar(t);
                                return (
                                    <button key={t.chave} type="button" className={`tp-card ${chave === t.chave ? 'is-on' : ''}`}
                                        aria-pressed={chave === t.chave}
                                        onClick={() => setChave(t.chave)} onDoubleClick={() => { setChave(t.chave); setPasso(2); }}>
                                        <span className="tp-card-tit">{t.icone} {t.nome}</span>
                                        <span className="tp-card-txt">{t.descricao}</span>
                                        <span className="tp-card-chips">
                                            <span className="tp-chip"><b>{n.etapas}</b> etapas</span>
                                            <span className="tp-chip"><b>{n.desfechos}</b> desfechos</span>
                                            {t.destaque && <span className="tp-chip is-tom">{t.destaque}</span>}
                                        </span>
                                    </button>
                                );
                            })}
                            <button type="button" className={`tp-card is-zero ${doZero ? 'is-on' : ''}`} aria-pressed={doZero}
                                onClick={() => setChave('zero')} onDoubleClick={() => { setChave('zero'); setPasso(2); }}>
                                <span className="tp-card-tit"><LuPlus size={16} /> Começar do zero</span>
                                <span className="tp-card-txt">As três etapas vazias, para escrever cada tarefa e cada desfecho.</span>
                            </button>
                        </div>
                    ) : (
                        <div className="tp-leitura">
                            {(doZero ? trilhoVazio() : escolhido!.etapas).map(e => (
                                <div key={e.statusId} className={`tp-leitura-et ${e.ativo ? '' : 'is-off'}`}>
                                    <div className="tp-leitura-h">
                                        <i className="pl-dot" style={{ backgroundColor: COR_ETAPA[e.statusId] }} aria-hidden="true" />
                                        <span className="tp-leitura-nm">{nomeEtapa(e.statusId)}</span>
                                        {!e.ativo && <span className="tp-leitura-off">sem tarefa padrão</span>}
                                    </div>
                                    {e.ativo && (
                                        <>
                                            <p className="tp-leitura-t">Tarefa: <b>{e.titulo}</b> · {e.prazoDiasUteis} {e.prazoDiasUteis === 1 ? 'dia útil' : 'dias úteis'}{e.tipoTarefaNome ? ` · ${e.tipoTarefaNome}` : ''}</p>
                                            <ul className="tp-leitura-des">
                                                {e.desfechos.map(d => (
                                                    <li key={d.rotulo}><b>{d.rotulo}</b> <span>{descreverAcao(d.acao, d.statusDestinoId, d.diasReagendamento)}</span></li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="tp-dlg-foot">
                    <span className="tp-passo">Passo {passo} de 2{passo === 1 ? ` · ${TRILHOS_PRONTOS.length} trilhos` : ''}</span>
                    {passo === 1 ? (
                        <>
                            <button type="button" className="lk-btn" onClick={onFechar}>Cancelar</button>
                            <button type="button" className="lk-btn is-primary" disabled={!chave} onClick={() => setPasso(2)}>Continuar <LuArrowRight size={14} /></button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="lk-btn" onClick={() => setPasso(1)} disabled={salvando}><LuArrowLeft size={14} /> Voltar</button>
                            <button type="button" className="lk-btn is-primary" onClick={confirmar} disabled={salvando}>
                                {salvando ? 'Aplicando…' : substituindo ? 'Substituir o trilho' : 'Usar este trilho'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EscolherTrilhoModal;
